// UnifiedOperators.js
// Intent-based operator system that simplifies UX by hiding type complexity from users

/**
 * Unified operators organized by user intent rather than data type.
 * Each operator specifies:
 * - label: What the user sees in the dropdown
 * - category: Grouping in the dropdown menu
 * - forceType: The value_type to set when this operator is selected (optional)
 * - op: The kv_relational_op value
 * - mappings: For auto-detect operators, maps detected type to op (optional)
 * - detectType: Whether to auto-detect type from value (optional)
 * - placeholder: Hint text for the value input field
 * - inputType: The type of input to render (text, number, date, list, object)
 */

export const unifiedOperators = [
  // Comparison - auto-detect type from value
  {
    id: 'equals',
    label: 'equals',
    category: 'Comparison',
    detectType: true,
    mappings: { str: 'eq', int: 'eq', dec: 'eq', '[str]': 'eq', dt: 'eq' },
    placeholder: 'Enter value',
    inputType: 'auto',
  },
  {
    id: 'not_equals',
    label: 'does not equal',
    category: 'Comparison',
    detectType: true,
    mappings: { str: 'ne', int: 'ne', dec: 'ne', '[str]': 'ne', dt: 'ne' },
    placeholder: 'Enter value',
    inputType: 'auto',
  },

  // Numeric comparisons - auto-detect int vs dec based on value
  {
    id: 'greater_than',
    label: 'is greater than',
    category: 'Numeric',
    detectType: true,
    numericOnly: true,
    mappings: { int: 'gt', dec: 'gt', str: 'gt' },
    defaultType: 'int',
    placeholder: 'Enter number',
    inputType: 'number',
  },
  {
    id: 'less_than',
    label: 'is less than',
    category: 'Numeric',
    detectType: true,
    numericOnly: true,
    mappings: { int: 'lt', dec: 'lt', str: 'lt' },
    defaultType: 'int',
    placeholder: 'Enter number',
    inputType: 'number',
  },
  {
    id: 'at_least',
    label: 'is at least',
    category: 'Numeric',
    detectType: true,
    numericOnly: true,
    mappings: { int: 'ge', dec: 'ge', str: 'ge' },
    defaultType: 'int',
    placeholder: 'Enter number',
    inputType: 'number',
  },
  {
    id: 'at_most',
    label: 'is at most',
    category: 'Numeric',
    detectType: true,
    numericOnly: true,
    mappings: { int: 'le', dec: 'le', str: 'le' },
    defaultType: 'int',
    placeholder: 'Enter number',
    inputType: 'number',
  },

  // Date comparisons - force dt type
  {
    id: 'is_on',
    label: 'is on',
    category: 'Date',
    forceType: 'dt',
    op: 'eq',
    placeholder: 'Select date',
    inputType: 'date',
  },
  {
    id: 'is_before',
    label: 'is before',
    category: 'Date',
    forceType: 'dt',
    op: 'lt',
    placeholder: 'Select date',
    inputType: 'date',
  },
  {
    id: 'is_after',
    label: 'is after',
    category: 'Date',
    forceType: 'dt',
    op: 'gt',
    placeholder: 'Select date',
    inputType: 'date',
  },
  {
    id: 'is_on_or_before',
    label: 'is on or before',
    category: 'Date',
    forceType: 'dt',
    op: 'le',
    placeholder: 'Select date',
    inputType: 'date',
  },
  {
    id: 'is_on_or_after',
    label: 'is on or after',
    category: 'Date',
    forceType: 'dt',
    op: 'ge',
    placeholder: 'Select date',
    inputType: 'date',
  },
  {
    id: 'is_not_on',
    label: 'is not on',
    category: 'Date',
    forceType: 'dt',
    op: 'ne',
    placeholder: 'Select date',
    inputType: 'date',
  },

  // Text operations - force str type
  {
    id: 'contains_text',
    label: 'contains text',
    category: 'Text',
    forceType: 'str',
    op: 'scon',
    placeholder: 'Enter text to search for',
    inputType: 'text',
  },
  {
    id: 'not_contains_text',
    label: 'does not contain text',
    category: 'Text',
    forceType: 'str',
    op: 'nscon',
    placeholder: 'Enter text to exclude',
    inputType: 'text',
  },
  {
    id: 'starts_with',
    label: 'starts with',
    category: 'Text',
    forceType: 'str',
    op: 'starts',
    placeholder: 'Enter prefix',
    inputType: 'text',
  },

  // List membership - force [str] type
  {
    id: 'is_in_list',
    label: 'is in list',
    category: 'List',
    forceType: '[str]',
    op: 'in',
    placeholder: 'value1,value2,value3',
    inputType: 'text',
    hint: 'Enter comma-separated values',
  },
  {
    id: 'is_not_in_list',
    label: 'is not in list',
    category: 'List',
    forceType: '[str]',
    op: 'nin',
    placeholder: 'value1,value2,value3',
    inputType: 'text',
    hint: 'Enter comma-separated values',
  },

];

// Category display order
export const categoryOrder = [
  'Comparison',
  'Numeric',
  'Date',
  'Text',
  'List',
];

/**
 * Detect the value type from a given value string.
 * Used for auto-detect operators like "equals" and "does not equal".
 */
export const detectValueType = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'str';
  }

  const strValue = String(value).trim();

  // Date pattern: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(strValue)) {
    return 'dt';
  }

  // Decimal number (has decimal point)
  if (/^-?\d+\.\d+$/.test(strValue)) {
    return 'dec';
  }

  // Integer number
  if (/^-?\d+$/.test(strValue)) {
    return 'int';
  }

  // Default to string
  return 'str';
};

/**
 * Get the operator definition by its ID
 */
export const getOperatorById = (id) => {
  return unifiedOperators.find((op) => op.id === id);
};

/**
 * Get the operator definition by value_type and kv_relational_op
 * Used for backward compatibility when loading existing rules
 */
export const getOperatorByTypeAndOp = (valueType, kvOp) => {
  // First try to find a direct match with forceType
  let operator = unifiedOperators.find(
    (op) => op.forceType === valueType && op.op === kvOp
  );

  if (operator) {
    return operator;
  }

  // For auto-detect operators, check mappings
  operator = unifiedOperators.find(
    (op) => op.detectType && op.mappings && op.mappings[valueType] === kvOp
  );

  if (operator) {
    return operator;
  }

  // Legacy operator mappings for backward compatibility
  const legacyMappings = {
    'str:eq': 'equals',
    'str:ne': 'not_equals',
    'str:con': 'contains_text',    // legacy contains -> new scon
    'str:ncon': 'not_contains_text', // legacy ncon -> new nscon
    'str:starts': 'starts_with',
    'int:eq': 'equals',
    'int:ne': 'not_equals',
    'int:gt': 'greater_than',
    'int:lt': 'less_than',
    'int:ge': 'at_least',
    'int:le': 'at_most',
    'dec:eq': 'equals',
    'dec:ne': 'not_equals',
    'dec:gt': 'greater_than',
    'dec:lt': 'less_than',
    'dec:ge': 'at_least',
    'dec:le': 'at_most',
    'dt:eq': 'is_on',
    'dt:ne': 'is_not_on',
    'dt:gt': 'is_after',
    'dt:lt': 'is_before',
    'dt:ge': 'is_on_or_after',
    'dt:le': 'is_on_or_before',
    '[str]:eq': 'equals',
    '[str]:ne': 'not_equals',
    '[str]:in': 'is_in_list',
    '[str]:nin': 'is_not_in_list',
    // obj type uses same operators as str - key with dot auto-sets value_type
    'obj:eq': 'equals',
    'obj:ne': 'not_equals',
    'obj:scon': 'contains_text',
    'obj:nscon': 'not_contains_text',
    'obj:in': 'is_in_list',
    'obj:nin': 'is_not_in_list',
    'obj:gt': 'greater_than',
    'obj:lt': 'less_than',
    'obj:ge': 'at_least',
    'obj:le': 'at_most',
    'obj:starts': 'starts_with',
  };

  const key = `${valueType}:${kvOp}`;
  const operatorId = legacyMappings[key];

  if (operatorId) {
    return getOperatorById(operatorId);
  }

  // Default to equals if nothing found
  return getOperatorById('equals');
};

/**
 * Get the value_type and kv_relational_op for a given operator and value
 * Returns { value_type, kv_relational_op }
 */
export const getTypeAndOp = (operator, value = '') => {
  if (!operator) {
    return { value_type: 'str', kv_relational_op: 'eq' };
  }

  // If operator has a forced type, use it
  if (operator.forceType) {
    return {
      value_type: operator.forceType,
      kv_relational_op: operator.op,
    };
  }

  // For auto-detect operators, detect type from value
  if (operator.detectType && operator.mappings) {
    let detectedType = detectValueType(value);

    // For numeric-only operators, constrain to int/dec types
    if (operator.numericOnly) {
      if (detectedType !== 'int' && detectedType !== 'dec') {
        // Use defaultType if value isn't a valid number (empty, string, etc.)
        detectedType = operator.defaultType || 'int';
      }
    }

    return {
      value_type: detectedType,
      kv_relational_op: operator.mappings[detectedType],
    };
  }

  // Fallback
  return { value_type: 'str', kv_relational_op: 'eq' };
};

/**
 * Get operators grouped by category for dropdown rendering
 */
export const getOperatorsByCategory = () => {
  const grouped = {};

  categoryOrder.forEach((category) => {
    grouped[category] = unifiedOperators.filter(
      (op) => op.category === category
    );
  });

  return grouped;
};

/**
 * Filter operators based on search text
 */
export const filterOperators = (searchText) => {
  if (!searchText || searchText.trim() === '') {
    return unifiedOperators;
  }

  const search = searchText.toLowerCase().trim();
  return unifiedOperators.filter(
    (op) =>
      op.label.toLowerCase().includes(search) ||
      op.category.toLowerCase().includes(search)
  );
};

/**
 * Get filtered operators grouped by category
 */
export const getFilteredOperatorsByCategory = (searchText) => {
  const filtered = filterOperators(searchText);
  const grouped = {};

  categoryOrder.forEach((category) => {
    const categoryOps = filtered.filter((op) => op.category === category);
    if (categoryOps.length > 0) {
      grouped[category] = categoryOps;
    }
  });

  return grouped;
};

export default unifiedOperators;
